import { FC, memo } from 'react';

import { notification as antNotification, Switch, message as antMessage, Tooltip } from 'antd';
import classNames from 'classnames';

import i18n from '@/content';

import PolicySetting, { PolicyRowProps } from '../PolicySetting';
import { NOTIFICATION_DURATION, EXTRA_SETTINGS_CONTENT } from './constants';
import {
    detectConflictOnToggle,
    getSettingsToDisable,
} from '../../../../../../stores/PolicyStore/helpers/incompatibleSettings/conflictDetection';
import { showConflictModal } from '../../../ConflictModal';
import { getEnforcedValue } from '../../../../../../stores/PolicyStore/helpers/incompatibleSettings/conflictConfig';
import { PolicySetting as PolicySettingType } from '../../../../../../types/types';
import styles from './PolicySwitchRow.module.scss';

type PolicySwitchRowProps = PolicyRowProps<boolean> & {
    allSettings?: PolicySettingType[];
    /** Disables the switch for product rules (e.g. default policy); independent of conflict enforcement */
    policyDisabled?: boolean;
    policyDisabledTooltip?: string;
};

const PolicySwitchRow: FC<PolicySwitchRowProps> = ({
    settingValue,
    settingId,
    handleChange,
    isChild,
    allSettings = [],
    policyDisabled = false,
    policyDisabledTooltip,
}) => {
    const extraContent = EXTRA_SETTINGS_CONTENT[settingId];
    const notification = extraContent?.notification;
    const bottomContent = extraContent?.bottomDescription;

    const enforcedValue = getEnforcedValue(settingId);
    const isEnforced = enforcedValue !== undefined;
    const switchDisabled = isEnforced || policyDisabled;

    const onToggle = (checked: boolean): void => {
        if (notification) {
            antNotification.destroy();
            if (notification.activationFlag === checked) {
                antNotification.info({
                    message: i18n.t(notification.titleKey),
                    description: i18n.t(notification.descriptionKey),
                    duration: NOTIFICATION_DURATION,
                });
            }
        }

        const conflict = detectConflictOnToggle(settingId, checked, allSettings);

        if (conflict) {
            if (conflict.rule.type === 'required_at_least_one' && !checked) {
                const messageKey = conflict.rule.messageKey || 'atLeastOneVerification';
                const nameSpace = 'policyEditor.messages.incompatibleSettingsWarnings';
                antMessage.warning(i18n.t(`${nameSpace}.${messageKey}`));
                return;
            }

            // For mutual exclusion and one-to-many conflicts, show confirmation modal
            const settingsToDisable = getSettingsToDisable(conflict);

            showConflictModal(
                conflict.message,
                () => handleChange(settingId, checked, settingsToDisable),
                () => {},
            );
        } else {
            handleChange(settingId, checked);
        }
    };

    return (
        <PolicySetting
            isChild={isChild}
            settingId={settingId}
            type="switch"
            bottomContent={bottomContent}
        >
            {policyDisabled && policyDisabledTooltip ? (
                <Tooltip title={policyDisabledTooltip}>
                    <span className={styles['switch-tooltip-anchor']}>
                        <Switch
                            className={classNames(
                                styles['policy-switch'],
                                settingValue ? styles.active : styles.inactive,
                            )}
                            data-testid="policyEditor_settingsTuggle"
                            onChange={onToggle}
                            checked={settingValue}
                            disabled={switchDisabled}
                        />
                    </span>
                </Tooltip>
            ) : (
                <Switch
                    className={classNames(
                        styles['policy-switch'],
                        settingValue ? styles.active : styles.inactive,
                    )}
                    data-testid="policyEditor_settingsTuggle"
                    onChange={onToggle}
                    checked={settingValue}
                    disabled={switchDisabled}
                />
            )}
        </PolicySetting>
    );
};

export default memo(PolicySwitchRow);
